import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { $fetch } from 'utils/fetch'

export function useNotifications() {
  const queryClient = useQueryClient()

  // Fetch notifications
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await $fetch('/v1/notifications', {
        query: { unread_only: false, limit: 50 },
      })
      return response.data?.notifications || []
    },
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  })

  // Get unread count
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-count'],
    queryFn: async () => {
      const response = await $fetch('/v1/notifications/unread-count')
      return response.data?.unread_count || 0
    },
    refetchInterval: 60 * 1000, // Refetch every minute
  })

  // Mark as read mutation
  const markAsRead = useMutation({
    mutationFn: async (notificationId) => {
      return await $fetch(`/v1/notifications/${notificationId}/read`, {
        method: 'PATCH',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications'])
      queryClient.invalidateQueries(['notifications-count'])
    },
  })

  // Mark all as read mutation
  const markAllAsRead = useMutation({
    mutationFn: async () => {
      return await $fetch('/v1/notifications/read-all', {
        method: 'PATCH',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications'])
      queryClient.invalidateQueries(['notifications-count'])
    },
  })

  // Delete a single notification
  const deleteNotification = useMutation({
    mutationFn: async (notificationId) => {
      return await $fetch(`/v1/notifications/${notificationId}`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications'])
      queryClient.invalidateQueries(['notifications-count'])
    },
  })

  // Delete all notifications
  const deleteAllNotifications = useMutation({
    mutationFn: async () => {
      return await $fetch('/v1/notifications', {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications'])
      queryClient.invalidateQueries(['notifications-count'])
    },
  })

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead: markAsRead.mutate,
    markAllAsRead: markAllAsRead.mutate,
    deleteNotification: deleteNotification.mutate,
    deleteAllNotifications: deleteAllNotifications.mutate,
  }
}
